import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_TYPES } from "~/constants/EditorTypes";
import {
  DEFAULT_TIER,
  getFallbackTier,
  TIER_STORAGE_KEY,
} from "~/constants/ModelTiers";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { countChatTokens } from "~/helpers/tokenUtils";
import { apiFetch, streamFetch } from "~/lib/fetchClient";
import { approximateTokenCount } from "../lib/countTokensService";
import insertContent from "../lib/insertContent";
import messagesToPlain from "../lib/messagesToPlain";
import normalizeProduct from "../lib/normalizeProduct";
import parseErrorMessage from "../lib/parseErrorMessage";
import substituteTemplatePlaceholders from "../lib/substituteTemplatePlaceholders";
import { ModalStates } from "../states/ModalStates";
import useConversationAPI from "./useConversationAPI";
import useVoiceInput from "./useVoiceInput";

const AI_SERVICE_BASE_URL = process.env.SHAKAPACKER_PUBLIC_AI_SERVICE_URL;

const TIER_LABELS = {
  fast: "Fast",
  standard: "Standard",
  reasoning: "Reasoning",
};

const getMaxTokens = () =>
  Number(process.env.SHAKAPACKER_PUBLIC_MAX_TOKENS_PER_CONVERSATION);

const getTextFromParts = (parts) =>
  parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");

// Walks a useChat-shaped messages array (array or messagesRef.current) from
// newest to oldest and returns the text body of the most recent assistant
// message, or "" when none exists. Module-level so the same scan does not
// have to be open-coded as an IIFE inside the hook (was duplicated 3× before
// extraction — applyLibraryTemplate, the depth-change re-substitution
// effect, and handleStop's partial-output capture).
const getLastAiMessage = (messages) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      return getTextFromParts(messages[i].parts);
    }
  }
  return "";
};

// BK-3229: a Library prompt's base `body` is email-oriented. Channel-specific
// rewrites live in `metadata.variants` as { name, condition, body } objects
// (e.g. { name: "sms", ... }). Pick the variant whose name matches the
// composer's channel (EDITOR_TYPES[editorType].channel); fall back to the
// base body for `email` and any channel with no authored variant.
const resolvePromptBody = (prompt, editorType) => {
  const channel = EDITOR_TYPES[editorType]?.channel;
  const variantBody = prompt.metadata?.variants?.find(
    (variant) => variant.name === channel,
  )?.body;
  return variantBody ?? prompt.body ?? "";
};

// Context fetch runs without a timeout — the user can send a message at any
// time.  Whatever context has loaded by then is included; if it hasn't
// finished yet the AI simply operates without it (same as before, but we no
// longer silently abort requests that would have succeeded given a few more
// seconds).

const SYSTEM_PROMPT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const MAX_BODY_BYTES = 1_000_000; // Server rejects > 1 MB

// BK-3229 R4 AC 7.1.2: a template-set selector is "overridden" when (a)
// the template declared a default for it AND (b) the value at completion
// time differs from that default. Module-level since pure and called
// from onFinish — extracted to keep onFinish focused on orchestration.
const wasOverridden = (templateValue, currentValue) =>
  templateValue != null && templateValue !== currentValue;

const validateInput = (text, totalTokens) => {
  const maxTokens = getMaxTokens();
  if (!text.trim()) {
    return "Prompt cannot be empty.";
  }
  if (new Blob([text]).size > MAX_BODY_BYTES) {
    return "Message is too large. Please shorten your input.";
  }
  if (totalTokens > maxTokens) {
    return `Input exceeds the maximum allowed tokens (${maxTokens}). Total tokens including current conversation: ${totalTokens}.`;
  }
  return null;
};

const useAIAssist = (
  editor,
  mode,
  setSubject,
  onClose,
  contextOptions = {},
) => {
  const markdownContentRef = useRef();
  const messagesRef = useRef([]);
  const tierRef = useRef(DEFAULT_TIER);
  const systemPromptRef = useRef("");
  // BK-3229 R4 / Decision 2: structured tree returned by /ai/compose_context
  // alongside system_prompt. Consumed by substituteTemplatePlaceholders to
  // fill {contact.X}/{account.X} tokens in Library template bodies on click.
  const substitutionsRef = useRef({});
  // Bumps every time fetchSystemPrompt updates substitutionsRef. Used by the
  // re-substitution effect below to react to depth-change refetches without
  // converting substitutions into render-driving state.
  const [substitutionsVersion, setSubstitutionsVersion] = useState(0);
  // Snapshot of the most recently click-applied Library template, kept so we
  // can re-run substituteTemplatePlaceholders when the substitutions tree
  // grows after a depth-bumping refetch (e.g. clicking a template that
  // declares default_context_depth: production while the modal was viewing
  // at sender_contact_details — applyLibraryTemplate substituted against the
  // pre-refetch tree, so {contact.relitix.X} tokens stayed literal). Reset
  // when the user types, picks a new template, or starts a fresh prompt.
  const lastTemplatePopulationRef = useRef({
    rawBody: null,
    populatedText: null,
    missingCount: 0,
  });
  const productRef = useRef(normalizeProduct(contextOptions.product));
  const editorTypeRef = useRef(contextOptions.editorType);
  const outputFormatRef = useRef(contextOptions.outputFormat);
  // BK-3229 R4 AC 7.1.2: read at onFinish via ref (not from the
  // contextOptions closure) so user_overrode_context_depth compares the
  // template default against the depth ACTUALLY in effect at completion
  // time, not the stale initial value at modal-open. Synced via the
  // useEffect below alongside tierRef / outputFormatRef.
  const contextDepthRef = useRef(contextOptions.contextDepth);
  const originalTierRef = useRef(null);
  const isRetryingRef = useRef(false);
  const promptMetadataRef = useRef({ promptVersions: null });
  const conversationIdRef = useRef(null);
  // Tracks the last library prompt the user picked, so we can tell whether
  // they edited it before sending. Cleared when starting a fresh prompt.
  // BK-3229 R4 AC 7.1.2: also captures the template's declared defaults
  // and category so onFinish can compute user_overrode_* + downstream
  // components can render category-aware UI (e.g. CommissionTierNotice).
  // `templateDefaultsApplied` records whether the auto-population actually
  // ran (controlled by the ai_assist_template_defaults feature flag) — when
  // false, templateDefaults is forced to all-null so user_overrode_* can
  // never spuriously fire from a template default the user never saw.
  const libraryPromptRef = useRef({
    id: null,
    originalText: null,
    category: null,
    templateDefaults: { model: null, context: null, format: null },
    templateDefaultsApplied: false,
  });
  // Frozen snapshot of library prompt state at send-time, so onFinish sees
  // the value as it was when the user hit generate.
  const currentGenerationLibraryRef = useRef({
    libraryPromptId: null,
    libraryPromptModified: null,
    templateDefaults: { model: null, context: null, format: null },
    templateDefaultsApplied: false,
  });

  // Common PostHog properties required on all AI Assist events (PRD Req 5)
  const getCommonEventProps = useCallback(
    () => ({
      editor_type: editorTypeRef.current,
      module: productRef.current,
      channel: EDITOR_TYPES[editorTypeRef.current]?.channel,
    }),
    [],
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${AI_SERVICE_BASE_URL}/generate`,
        fetch: streamFetch,
        prepareSendMessagesRequest: ({
          id,
          messages: msgs,
          trigger,
          messageId,
        }) => {
          return {
            body: {
              id,
              messages: msgs,
              trigger,
              messageId,
              tier: tierRef.current,
              systemPrompt: systemPromptRef.current,
              product: productRef.current || undefined,
              editorType: editorTypeRef.current || undefined,
              outputFormat: outputFormatRef.current || undefined,
            },
          };
        },
      }),
    [],
  );

  const [inputText, setInputText] = useState("");
  const inputBeforeVoiceRef = useRef("");
  const [retryPending, setRetryPending] = useState(false);
  const [feedbackState, setFeedbackState] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [inputError, setInputError] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [serverTokensCount, setServerTokensCount] = useState(null);
  const [tierFallbackNotice, setTierFallbackNotice] = useState(null);
  const [systemPromptLoading, setSystemPromptLoading] = useState(false);
  // BK-3229 R1 AC 3.1.2: Compose-tab Commission inline notice needs to
  // know the active prompt's category. Mirrors libraryPromptRef.current.category
  // but as state so React re-renders the notice when category changes.
  const [activePromptCategory, setActivePromptCategory] = useState(null);
  const systemPromptCacheRef = useRef({
    leadId: null,
    depth: null,
    editorType: null,
    prompt: null,
    timestamp: 0,
  });
  const lastAssistantMessageIdRef = useRef(null);
  const preGenEstimateRef = useRef(null);
  const lastFinishedMessageRef = useRef(null);
  const [hasEstimatedUsage, setHasEstimatedUsage] = useState(false);

  const {
    voiceState,
    isSupported: voiceSupported,
    toggleVoiceInput,
    stopRecognition,
    errorMessage: voiceError,
  } = useVoiceInput({
    onTranscript: useCallback((text) => {
      const prefix = inputBeforeVoiceRef.current;
      setInputText(prefix ? `${prefix} ${text}` : text);
    }, []),
    onStart: useCallback(() => {
      inputBeforeVoiceRef.current = inputText;
    }, [inputText]),
    eventProps: getCommonEventProps(),
  });

  // Pre-generation estimate from the AI service tokenizer and message overhead.
  const [contextTokenCount, setContextTokenCount] = useState(null);
  const [isTokenEstimateApproximate, setIsTokenEstimateApproximate] =
    useState(true);
  const tokenRequestIdRef = useRef(0);

  const showModelSelector = checkFeatureFlag(
    process.env.SHAKAPACKER_PUBLIC_AI_ASSIST_MODEL_SELECTOR_FLAG,
  );
  const [tier, setTier] = useState(() => {
    if (!showModelSelector) {
      return DEFAULT_TIER;
    }
    return localStorage.getItem(TIER_STORAGE_KEY) || DEFAULT_TIER;
  });

  const fetchTokenEstimate = useCallback(async (prompt, messages) => {
    const requestId = ++tokenRequestIdRef.current;
    const { default: countTokensFromService } = await import(
      "../lib/countTokensService"
    );

    const result = await countTokensFromService(prompt, messages, {
      tier: tierRef.current,
      product: productRef.current,
      editorType: editorTypeRef.current,
      outputFormat: outputFormatRef.current,
    });

    // Guard against stale responses
    if (requestId !== tokenRequestIdRef.current) {
      return;
    }

    if (result?.inputTokens != null) {
      setContextTokenCount(result.inputTokens);
      setIsTokenEstimateApproximate(result.isApproximate ?? true);
    } else {
      // Fallback: approximate from character length
      const messagesText = messages.map((m) => m.content || "").join(" ");
      setContextTokenCount(
        approximateTokenCount(prompt) + approximateTokenCount(messagesText),
      );
      setIsTokenEstimateApproximate(true);

      captureEvent("ai_assist_token_count_fallback", {
        ...getCommonEventProps(),
        system_prompt_length: prompt?.length || 0,
        message_count: messages.length,
        tier: tierRef.current,
      });
    }
  }, []);

  const fetchSystemPrompt = useCallback(async (leadId, contextDepth) => {
    const cache = systemPromptCacheRef.current;
    const now = Date.now();
    const currentEditorType = editorTypeRef.current;

    if (
      cache.leadId === leadId &&
      cache.depth === contextDepth &&
      cache.editorType === currentEditorType &&
      cache.prompt &&
      now - cache.timestamp < SYSTEM_PROMPT_CACHE_TTL
    ) {
      systemPromptRef.current = cache.prompt;
      return cache.prompt;
    }

    try {
      setSystemPromptLoading(true);

      const body = {
        lead_id: leadId,
        context_depth: contextDepth,
        editor_type: editorTypeRef.current,
      };
      // AC 9.4a: Pass conversation_id so anti-repetition excludes messages
      // already in the current conversation's messages array.
      if (conversationIdRef.current) {
        body.conversation_id = conversationIdRef.current;
      }
      const data = await apiFetch.post("/ai/compose_context", {
        json: body,
      });
      const prompt = data.system_prompt || "";
      // BK-3229 R4 / Decision 2: capture the substitutions tree alongside
      // system_prompt. Empty fallback ({}) is safe — substituteTemplatePlaceholders
      // leaves all tokens literal when the tree is empty.
      substitutionsRef.current = data.substitutions || {};
      setSubstitutionsVersion((v) => v + 1);
      systemPromptCacheRef.current = {
        leadId,
        depth: contextDepth,
        editorType: currentEditorType,
        prompt,
        timestamp: Date.now(),
      };
      systemPromptRef.current = prompt;

      const userSignature = Rails.helpers.currentUser?.signature;
      let ctaSource = "landing_page";
      if (prompt.includes("CTA Link:")) {
        ctaSource = "user_cta_link";
      } else if (prompt.includes("Default CTA:")) {
        ctaSource = "account_cta";
      }

      captureEvent("ai_assist_contact_context_loaded", {
        ...getCommonEventProps(),
        contact_id: leadId,
        context_depth: contextDepth,
        has_production_data: prompt.includes("Production Data:"),
        has_relitix_data: prompt.includes("Production (Relitix):"),
        has_courted_data: prompt.includes("Production (Courted):"),
        context_length: prompt.length,
        context_token_count_estimate: Math.ceil(prompt.length / 4),
        cta_source: ctaSource,
        signature_mode: userSignature ? "user_signature" : "generated_signoff",
      });

      const hasAntiRepetition = prompt.includes("<ANTI_REPETITION>");
      if (hasAntiRepetition) {
        captureEvent("ai_assist_anti_repetition_applied", {
          ...getCommonEventProps(),
          contact_id: leadId,
          previous_message_count: (prompt.match(/<previous_message_/g) || [])
            .length,
          context_depth: contextDepth,
        });
      }

      // Refresh the token estimate in parallel (non-blocking)
      fetchTokenEstimate(prompt, messagesToPlain(messagesRef.current));

      return prompt;
    } catch (error) {
      // Fallback: AI operates without contact context
      console.warn("compose_context failed, using fallback", error);
      systemPromptRef.current = "";
      // Same fallback for substitutions — helper leaves all {contact.X}/
      // {account.X} tokens literal when the tree is empty.
      substitutionsRef.current = {};
      setSubstitutionsVersion((v) => v + 1);
      setContextTokenCount(0);
      setIsTokenEstimateApproximate(true);
      return "";
    } finally {
      setSystemPromptLoading(false);
    }
  }, []);

  const {
    messages,
    status: chatStatus,
    error: chatError,
    sendMessage,
    stop,
    setMessages,
    clearError,
    regenerate,
  } = useChat({
    transport,
    experimental_throttle: 50,
    onFinish: async ({
      message,
      messages: finishedMessages,
      isAbort,
      isError,
      isDisconnect,
    }) => {
      const messageKey = message.id ?? message;
      if (lastFinishedMessageRef.current === messageKey) {
        return;
      }
      lastFinishedMessageRef.current = messageKey;
      const interrupted = isAbort || isError || isDisconnect;
      const failed = isError || isDisconnect;
      const textContent = getTextFromParts(message.parts);
      if (!textContent) {
        return;
      }

      // onError may have scheduled a tier fallback. Its failed attempt must
      // not clear the retry state or report a successful generation.
      if (!failed) {
        isRetryingRef.current = false;
        setInputText("");
      }
      setHasGenerated(true);

      promptMetadataRef.current = {
        promptVersions: message.metadata?.prompt_versions ?? null,
      };

      const totalTokens = message.metadata?.usage?.totalTokens ?? null;

      // BK-3229 R1: `source` powers the library funnel (library → generation_completed).
      // `prompt_id` exposes the template id when the generation came from Library.
      const libraryPromptId =
        currentGenerationLibraryRef.current.libraryPromptId ?? null;

      // BK-3229 R4 AC 7.1.2: per-selector override-rate telemetry. The
      // module-level wasOverridden helper returns true only when (a) the
      // template declared a default for that selector AND (b) the value
      // at completion time differs from that default. When the
      // ai_assist_template_defaults flag is off, templateDefaults is
      // snapshotted as all-null at click time so user_overrode_* is
      // structurally always false for that path — protects the dashboard
      // from flag-off noise. template_defaults_enabled mirrors that
      // snapshot so the dashboard can segment further if needed.
      // `was_plan_fallback` is hardcoded false until Gap 4
      // (plan-eligibility check at template-click time) ships.
      const finalModelTier = message.metadata?.model_tier ?? tierRef.current;
      const finalContextDepth = contextDepthRef.current ?? null;
      const finalFormat = outputFormatRef.current ?? null;
      const tplDefaults =
        currentGenerationLibraryRef.current.templateDefaults ?? {};

      if (!interrupted) {
        captureEvent("ai_assist_generation_completed", {
          ...getCommonEventProps(),
          model_tier: finalModelTier,
          model: message.metadata?.model ?? null,
          provider: message.metadata?.provider ?? null,
          system_prompt_versions: promptMetadataRef.current.promptVersions,
          library_prompt_id: libraryPromptId,
          library_prompt_modified:
            currentGenerationLibraryRef.current.libraryPromptModified,
          source: libraryPromptId ? "library" : "compose",
          prompt_id: libraryPromptId,
          finish_reason: message.metadata?.finish_reason ?? null,
          output_format: finalFormat,
          input_tokens: message.metadata?.usage?.inputTokens ?? null,
          output_tokens: message.metadata?.usage?.outputTokens ?? null,
          total_tokens: totalTokens,
          cache_read_tokens: message.metadata?.usage?.cacheReadTokens ?? null,
          reasoning_tokens: message.metadata?.usage?.reasoningTokens ?? null,
          user_overrode_model_tier: wasOverridden(
            tplDefaults.model,
            finalModelTier,
          ),
          user_overrode_context_depth: wasOverridden(
            tplDefaults.context,
            finalContextDepth,
          ),
          user_overrode_format: wasOverridden(tplDefaults.format, finalFormat),
          template_defaults_enabled:
            currentGenerationLibraryRef.current.templateDefaultsApplied ??
            false,
          was_plan_fallback: false,
        });
      }
      // SDK callback history contains the final assistant message. Use it
      // directly so estimates never append the same output twice.
      const completedMessages = finishedMessages ?? [
        ...messagesRef.current.filter((msg) => msg.id !== message.id),
        message,
      ];
      const callTokens =
        totalTokens ??
        countChatTokens(messagesToPlain(completedMessages)) +
          approximateTokenCount(systemPromptRef.current);
      setServerTokensCount((prev) => (prev ?? 0) + callTokens);
      if (totalTokens == null) {
        setHasEstimatedUsage(true);
      }
      if (totalTokens != null && !interrupted) {
        // Track estimate vs actual accuracy (AC 9.2 analytics)
        if (preGenEstimateRef.current != null) {
          const estimated = preGenEstimateRef.current;
          const accuracyPct =
            estimated > 0 && totalTokens > 0
              ? Math.round(
                  (1 - Math.abs(totalTokens - estimated) / totalTokens) * 100,
                )
              : 0;
          captureEvent("ai_assist_token_actual", {
            ...getCommonEventProps(),
            actual_tokens: totalTokens,
            estimated_tokens: estimated,
            accuracy_pct: accuracyPct,
            context_depth: contextOptions?.contextDepth,
            system_prompt_versions: promptMetadataRef.current.promptVersions,
            output_format: outputFormatRef.current,
          });
          preGenEstimateRef.current = null;
        }
      }

      if (interrupted) {
        captureEvent("ai_assist_generation_interrupted", {
          ...getCommonEventProps(),
          model_tier: finalModelTier,
          stopped: Boolean(isAbort),
          failed: Boolean(isError),
          disconnected: Boolean(isDisconnect),
          estimated_tokens: totalTokens == null ? callTokens : null,
          actual_tokens: totalTokens,
          is_approximate: totalTokens == null,
        });
      }
      preGenEstimateRef.current = null;
      if (!failed) {
        originalTierRef.current = null;
      }

      try {
        const savedMsg = await conversationAPI.saveAssistantMessage(
          { role: "assistant", content: textContent },
          completedMessages,
          callTokens,
          finalModelTier,
        );
        if (savedMsg?.id && lastFinishedMessageRef.current === messageKey) {
          lastAssistantMessageIdRef.current = savedMsg.id;
        }
      } catch (error) {
        // The SDK does not await this callback. Handle persistence errors here.
        setLocalError(
          error.message || "Failed to save the generated response.",
        );
      }
    },
    onError: (error) => {
      // Server rejections (rate limit, duplicate, payload too large) are not
      // model errors — skip tier fallback and show the error directly.
      const status = error?.status;
      if (status === 429 || status === 409 || status === 413) {
        isRetryingRef.current = false;
        setHasGenerated(true);
        return;
      }

      const fallback = getFallbackTier(tierRef.current);
      if (fallback) {
        if (!originalTierRef.current) {
          originalTierRef.current = tierRef.current;
        }
        isRetryingRef.current = true;
        tierRef.current = fallback;
        setTier(fallback);
        setTierFallbackNotice(
          `${TIER_LABELS[originalTierRef.current]} model unavailable — using ${TIER_LABELS[fallback]} instead.`,
        );
        clearError();
        setRetryPending(true);
        return;
      }

      isRetryingRef.current = false;
      if (originalTierRef.current) {
        originalTierRef.current = null;
      }
      setHasGenerated(true);
    },
  });

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Deferred tier fallback retry — regenerate() can't be called from onError.
  useEffect(() => {
    if (!retryPending) {
      return;
    }
    setRetryPending(false);
    Promise.resolve(regenerate()).catch(() => {
      isRetryingRef.current = false;
      originalTierRef.current = null;
    });
  }, [retryPending]);

  useEffect(() => {
    fetchSystemPrompt(contextOptions.leadId, contextOptions.contextDepth);
  }, [contextOptions.leadId, contextOptions.contextDepth, fetchSystemPrompt]);

  // Re-run substituteTemplatePlaceholders against the freshly-fetched
  // substitutions tree when a depth-bumping refetch lands after the user
  // clicked a Library template. applyLibraryTemplate substitutes against the
  // pre-refetch tree synchronously — if that tree was missing branches the
  // new depth would unlock (e.g. relitix at production+), the click left the
  // {contact.relitix.X} token literal. Once the new tree arrives, retry the
  // substitution and only swap the textarea contents if (a) the user has not
  // edited since, and (b) the new run resolves more tokens than the click-
  // time run did.
  useEffect(() => {
    if (substitutionsVersion === 0) {
      return;
    }
    const populated = lastTemplatePopulationRef.current;
    if (populated.rawBody === null) {
      return;
    }
    if (inputText !== populated.populatedText) {
      return;
    }

    try {
      const result = substituteTemplatePlaceholders(
        populated.rawBody,
        substitutionsRef.current,
        getLastAiMessage(messagesRef.current),
      );
      if (result.missingTokens.length >= populated.missingCount) {
        return;
      }
      lastTemplatePopulationRef.current = {
        rawBody: populated.rawBody,
        populatedText: result.text,
        missingCount: result.missingTokens.length,
      };
      libraryPromptRef.current = {
        ...libraryPromptRef.current,
        originalText: result.text,
      };
      setInputText(result.text);
    } catch {
      // Same fail-safe as applyLibraryTemplate — never crash the modal from
      // a substitution retry. The literal token stays visible and the user
      // can still edit it manually.
    }
    // Intentionally only react to substitutionsVersion bumps — running on
    // every inputText keystroke would fight the user's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [substitutionsVersion]);

  useEffect(() => {
    tierRef.current = tier;
    productRef.current = normalizeProduct(contextOptions.product);
    editorTypeRef.current = contextOptions.editorType;
    outputFormatRef.current = contextOptions.outputFormat;
    contextDepthRef.current = contextOptions.contextDepth;
  }, [
    tier,
    contextOptions.product,
    contextOptions.editorType,
    contextOptions.outputFormat,
    contextOptions.contextDepth,
  ]);

  // Re-fetch token estimate when tier, outputFormat, product, or editorType
  // changes — different YAML prompt layers change the token count.
  // contextDepth changes already trigger re-fetch via fetchSystemPrompt above.
  useEffect(() => {
    if (systemPromptRef.current) {
      fetchTokenEstimate(
        systemPromptRef.current,
        messagesToPlain(messagesRef.current),
      );
    }
  }, [tier, contextOptions.outputFormat, fetchTokenEstimate]);

  const conversationAPI = useConversationAPI(messagesToPlain, messages, {
    leadId: contextOptions.leadId,
    product: contextOptions.product,
    editorType: contextOptions.editorType,
    contextDepth: contextOptions.contextDepth,
  });

  // Keep conversationIdRef in sync for use in fetchSystemPrompt (AC 9.4a)
  useEffect(() => {
    conversationIdRef.current = conversationAPI.conversationId;
  }, [conversationAPI.conversationId]);

  const outputText = useMemo(() => getLastAiMessage(messages), [messages]);

  const tokensCount = useMemo(() => {
    const isGenerating =
      chatStatus === "submitted" || chatStatus === "streaming";

    if (isGenerating) {
      return serverTokensCount ?? 0;
    }

    // After generation: use server-reported actual tokens (AC 9.3)
    if (serverTokensCount != null) {
      if (chatStatus === "ready" && !hasGenerated && inputText) {
        return serverTokensCount + approximateTokenCount(inputText);
      }
      return serverTokensCount;
    }

    // Before generation: use /count_tokens context + local user input estimate (AC 9.1)
    if (contextTokenCount != null) {
      const userTokens = inputText ? approximateTokenCount(inputText) : 0;
      return contextTokenCount + userTokens;
    }

    // Fallback: old heuristic (before /count_tokens response arrives)
    const plainMessages = messagesToPlain(messages);
    if (chatStatus === "ready" && !hasGenerated && inputText) {
      plainMessages.push({ role: "user", content: inputText });
    }
    return countChatTokens(plainMessages);
  }, [
    messages,
    inputText,
    serverTokensCount,
    contextTokenCount,
    chatStatus,
    hasGenerated,
  ]);

  const modalStatus = useMemo(() => {
    if ((chatError || localError) && !isRetryingRef.current) {
      return ModalStates.ERROR;
    }
    if (chatStatus === "submitted" || chatStatus === "streaming") {
      return ModalStates.GENERATING;
    }
    if (chatStatus === "ready" && hasGenerated) {
      return ModalStates.FINISHED;
    }
    return ModalStates.INITIAL;
  }, [chatStatus, chatError, localError, hasGenerated]);

  useEffect(() => {
    if (modalStatus !== ModalStates.GENERATING) {
      return;
    }
    const handleBeforeUnload = () => stop();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [modalStatus]);

  const conversationLimitExceeded = tokensCount > getMaxTokens();
  const subscriptionLimitExceeded =
    conversationAPI.subscriptionLimit !== null &&
    conversationAPI.totalTokensUsed >= conversationAPI.subscriptionLimit;

  const state = useMemo(
    () => ({
      isLoading: conversationAPI.isLoading,
      contextLoading: systemPromptLoading,
      inputText,
      outputText,
      conversationId: conversationAPI.conversationId,
      status: modalStatus,
      feedbackState,
      feedbackText,
      feedbackSending,
      messages,
      ...parseErrorMessage(
        isRetryingRef.current ? null : chatError?.message || localError || null,
      ),
      inputError,
      tokensCount,
      isTokenEstimateApproximate:
        serverTokensCount == null
          ? isTokenEstimateApproximate
          : hasEstimatedUsage,
      subscriptionLimit: conversationAPI.subscriptionLimit,
      totalTokensUsed: conversationAPI.totalTokensUsed,
      conversationLimitExceeded,
      subscriptionLimitExceeded,
      tier,
      tierFallbackNotice,
      showModelSelector,
      voiceState,
      voiceSupported,
      voiceError,
      activePromptCategory,
    }),
    [
      conversationAPI.isLoading,
      systemPromptLoading,
      inputText,
      outputText,
      conversationAPI.conversationId,
      modalStatus,
      feedbackState,
      feedbackText,
      feedbackSending,
      messages,
      chatError,
      localError,
      inputError,
      tokensCount,
      activePromptCategory,
      isTokenEstimateApproximate,
      hasEstimatedUsage,
      serverTokensCount,
      conversationAPI.subscriptionLimit,
      conversationAPI.totalTokensUsed,
      conversationLimitExceeded,
      subscriptionLimitExceeded,
      tier,
      tierFallbackNotice,
      voiceState,
      voiceSupported,
      voiceError,
    ],
  );

  const dispatch = useCallback((action) => {
    switch (action.type) {
      case "SET_FEEDBACK_STATE":
        setFeedbackState(action.payload);
        break;
      case "SET_FEEDBACK_TEXT":
        setFeedbackText(action.payload);
        break;
      case "SET_FEEDBACK_SENDING":
        setFeedbackSending(action.payload);
        break;
      default:
        break;
    }
  }, []);

  const clearAllErrors = () => {
    setInputError(null);
    setLocalError(null);
    clearError();
  };

  const generateAndSend = async (text) => {
    try {
      setTierFallbackNotice(null);
      const id = await conversationAPI.ensureConversation();
      if (!id) {
        setLocalError("Failed to create conversation. Please try again.");
        return;
      }
      await conversationAPI.saveUserMessage(text, tier);
      setHasGenerated(true);
      sendMessage({ text });
      captureEvent("ai_assist_generation_started", {
        ...getCommonEventProps(),
        model_tier: tierRef.current,
        output_format: outputFormatRef.current,
        library_prompt_id: currentGenerationLibraryRef.current.libraryPromptId,
        library_prompt_modified:
          currentGenerationLibraryRef.current.libraryPromptModified,
      });
    } catch (error) {
      setLocalError(error.message || "An unexpected error occurred.");
    }
  };

  const handleGenerateText = async () => {
    const text = inputText;
    const validationError = validateInput(text, tokensCount);
    if (validationError) {
      setInputError(validationError);
      return;
    }

    // Freeze library-prompt provenance at send-time so onFinish reports
    // the state as it was when the user actually hit generate.
    const libState = libraryPromptRef.current;
    currentGenerationLibraryRef.current = libState.id
      ? {
          libraryPromptId: libState.id,
          libraryPromptModified:
            (text ?? "").trim() !== (libState.originalText ?? "").trim(),
          templateDefaults: libState.templateDefaults ?? {
            model: null,
            context: null,
            format: null,
          },
          templateDefaultsApplied: libState.templateDefaultsApplied ?? false,
        }
      : {
          libraryPromptId: null,
          libraryPromptModified: null,
          templateDefaults: { model: null, context: null, format: null },
          templateDefaultsApplied: false,
        };

    // Track pre-generation token estimate (AC 9.5 analytics)
    preGenEstimateRef.current = tokensCount;
    captureEvent("ai_assist_token_estimate", {
      ...getCommonEventProps(),
      estimated_tokens: tokensCount,
      is_approximate:
        serverTokensCount == null
          ? isTokenEstimateApproximate
          : hasEstimatedUsage,
      context_depth: contextOptions?.contextDepth,
    });

    clearAllErrors();
    await generateAndSend(text);
  };

  const handleRegenerate = async () => {
    const maxTokens = getMaxTokens();
    if (tokensCount > maxTokens) {
      setInputError(`Token limit exceeded (${maxTokens}). Start a new prompt.`);
      return;
    }
    setTierFallbackNotice(null);
    setFeedbackState(null);
    setFeedbackText("");
    setFeedbackSending(false);
    clearAllErrors();
    setHasGenerated(true);
    try {
      await regenerate();
    } catch (error) {
      setLocalError(error.message || "An unexpected error occurred.");
    }
  };

  const handleNewPrompt = () => {
    stopRecognition();
    stop();
    clearAllErrors();
    setFeedbackState(null);
    setFeedbackText("");
    setFeedbackSending(false);
    setMessages([]);
    setInputText("");
    libraryPromptRef.current = {
      id: null,
      originalText: null,
      category: null,
      templateDefaults: { model: null, context: null, format: null },
      templateDefaultsApplied: false,
    };
    lastTemplatePopulationRef.current = {
      rawBody: null,
      populatedText: null,
      missingCount: 0,
    };
    setActivePromptCategory(null);
    currentGenerationLibraryRef.current = {
      libraryPromptId: null,
      libraryPromptModified: null,
      templateDefaults: { model: null, context: null, format: null },
      templateDefaultsApplied: false,
    };
    setHasGenerated(false);
    setServerTokensCount(null);
    setHasEstimatedUsage(false);
    setTierFallbackNotice(null);
    setRetryPending(false);
    isRetryingRef.current = false;
    originalTierRef.current = null;
    conversationAPI.resetConversation();
  };

  const handleClose = () => {
    stopRecognition();
    if (modalStatus === ModalStates.GENERATING) {
      stop();
    }
    onClose();
  };

  /**
   * Apply a Library template to the Compose textarea.
   *
   * BK-3229 R4 / Decision 2: resolves {contact.X}/{account.X} placeholders
   * in the template body against the substitutions tree from
   * /ai/compose_context, then setInputText so the user sees the resolved
   * text and can review/edit before sending. Missing values leave the
   * literal token visible — telemetry surfaces unresolved tokens for
   * future template refinement.
   *
   * Wrapped in try/catch — substituteTemplatePlaceholders is a pure regex
   * sweep with defensive null/empty handling, so an exception is not
   * expected, but the AI Assist modal must never crash from a Library
   * click. Falls back to the raw body on error.
   *
   * @param {{id: string|number|null, body: string|null}} prompt - Library template
   * @param {{applyDefaults?: boolean}} [options] - When applyDefaults is
   *   false (set by ModalContent when the ai_assist_template_defaults flag
   *   is off), templateDefaults is snapshotted as all-null so onFinish's
   *   user_overrode_* computation can never fire spuriously based on a
   *   template default the user never had applied.
   */
  const applyLibraryTemplate = (prompt, { applyDefaults = true } = {}) => {
    const rawBody = resolvePromptBody(prompt, editorTypeRef.current);

    let substitutedBody = rawBody;
    let missingTokens = [];
    try {
      const result = substituteTemplatePlaceholders(
        rawBody,
        substitutionsRef.current,
        getLastAiMessage(messagesRef.current),
      );
      substitutedBody = result.text;
      missingTokens = result.missingTokens;
    } catch (error) {
      console.warn(
        "substituteTemplatePlaceholders failed, falling back to raw body",
        error,
      );
    }

    libraryPromptRef.current = {
      id: prompt.id ?? null,
      originalText: substitutedBody,
      // BK-3229 R4 AC 7.1.2 / R1 AC 3.1.2: snapshot the template's category
      // (for category-aware UI like the Commission inline notice) and its
      // declared defaults (for user_overrode_* override-rate telemetry on
      // ai_assist_generation_completed). When applyDefaults is false the
      // selectors were never moved, so templateDefaults is forced null —
      // wasOverridden(null, anything) returns false, protecting the
      // override-rate dashboard from flag-off noise.
      category: prompt.category?.slug ?? null,
      templateDefaults: applyDefaults
        ? {
            model: prompt.default_model_tier ?? null,
            context: prompt.default_context_depth ?? null,
            format: prompt.default_format ?? null,
          }
        : { model: null, context: null, format: null },
      templateDefaultsApplied: applyDefaults,
    };
    setActivePromptCategory(prompt.category?.slug ?? null);
    // Snapshot for the depth-change re-substitution effect: if the template
    // declared a default_context_depth that triggers a refetch with a richer
    // substitutions tree, the effect compares against rawBody+populatedText
    // and only swaps the textarea when (a) the user hasn't typed and (b) the
    // new run resolves more tokens than this click-time run did.
    lastTemplatePopulationRef.current = {
      rawBody,
      populatedText: substitutedBody,
      missingCount: missingTokens.length,
    };
    setInputText(substitutedBody);

    const totalTokens = (
      rawBody.match(/\{(contact|account)\.[\w.]+\}|\{last_ai_message\}/g) || []
    ).length;
    if (totalTokens > 0) {
      captureEvent("ai_assist_template_substitution_applied", {
        ...getCommonEventProps(),
        library_prompt_id: prompt.id ?? null,
        total_tokens: totalTokens,
        substituted_count: totalTokens - missingTokens.length,
        missing_count: missingTokens.length,
        missing_tokens: missingTokens,
      });
    }
  };

  /**
   * Compose-tab population entrypoint. Two shapes:
   *   - String: plain text insertion (e.g. from Suggestions tab) — set as-is.
   *   - Object {id, body}: Library template — runs applyLibraryTemplate to
   *     resolve placeholders before populating the textarea.
   *
   * Memoized via useCallback because ModalContent.jsx puts this in a
   * useCallback deps array (line 119) — without memoization every render
   * of useAIAssist breaks downstream memoization of handleLibraryPromptClick
   * and forces PromptsList to re-render.
   *
   * Stale-closure safe: all mutable state read inside applyLibraryTemplate
   * comes from refs (messagesRef, substitutionsRef, libraryPromptRef) so
   * the empty deps array is correct.
   */
  const handlePromptClick = useCallback((prompt, options = {}) => {
    if (prompt && typeof prompt === "object") {
      applyLibraryTemplate(prompt, options);
    } else {
      libraryPromptRef.current = {
        id: null,
        originalText: null,
        category: null,
        templateDefaults: { model: null, context: null, format: null },
        templateDefaultsApplied: false,
      };
      // Plain-string population (e.g. Suggestions tab) has no template body
      // to re-substitute against — clear the snapshot so the depth-change
      // effect leaves the user's text alone.
      lastTemplatePopulationRef.current = {
        rawBody: null,
        populatedText: null,
        missingCount: 0,
      };
      setActivePromptCategory(null);
      setInputText(prompt ?? "");
    }
    // applyLibraryTemplate is intentionally not in deps — it reads only refs
    // and module-scoped helpers. See JSDoc above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInsert = async () => {
    if (modalStatus === ModalStates.GENERATING) {
      return;
    }
    // BK-3229: call scripts are spoken on a phone call, not signed like an
    // email — never append the user's email signature when inserting one.
    const skipSignature = libraryPromptRef.current.category === "call_scripts";
    insertContent(
      outputText,
      markdownContentRef,
      editor,
      mode,
      setSubject,
      outputFormatRef.current,
      skipSignature,
    );

    if (lastAssistantMessageIdRef.current) {
      try {
        await apiFetch.patch(
          `/ai/messages/${lastAssistantMessageIdRef.current}/mark_inserted`,
        );
      } catch {
        // Non-critical — anti-repetition is best-effort
      }
    }
    systemPromptCacheRef.current = {
      ...systemPromptCacheRef.current,
      timestamp: 0,
    };

    onClose();
  };

  return {
    state,
    dispatch,
    markdownContentRef,
    setTier,
    handleInputChange: (e) => setInputText(e.target.value),
    handlePromptClick,
    handleNewPrompt,
    // The SDK always finalizes an active response on abort; onFinish owns
    // partial persistence and usage accounting, including immediate stops.
    handleStop: stop,
    handleClose,
    handleInsert,
    handleGenerateText,
    handleRegenerate,
    handleRetry: () => handleGenerateText(),
    toggleVoiceInput,
    fetchTokensUsage: conversationAPI.fetchTokensUsage,
  };
};

export default useAIAssist;
